import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  FluentProvider,
} from '@fluentui/react-components';
import {
  AddCircle24Regular,
  ArrowClockwiseDashesColor,
  DeleteRegular,
  Edit24Regular,
} from '@fluentui/react-icons';
import {
  WORK_AVAILABILITY_PRIORITIES,
  formatDate,
  formatWorkAvailabilityDuration,
  getApiErrorMessage,
  parseWorkAvailabilityPriority,
  type IPagedCollection,
  type IWorkAvailabilityWindow,
} from '@moko/utils';
import { useActiveTheme } from '../theme/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import PagedDataGrid, {
  type ICommandBarContext,
  type ICommandBarItem,
  type IPagedDataGridRefType,
} from '../DataGrid/PagedDataGrid';
import type { DataGridColumn, IRowMenuOption } from '../DataGrid/DataGridUI';
import PagedCardList, {
  type ICardCommandBarItem,
  type PagedCardListRef,
} from '../CardList/PagedCardList';
import type { IWorkAvailabilityServices } from './types';
import WorkAvailabilityDrawer from './WorkAvailabilityDrawer';

const PAGE_SIZE = 10;

type WorkAvailabilityListItem = IWorkAvailabilityWindow & {
  [key: string]: unknown;
};

const priorityLabel = (
  priority: unknown,
  t: (key: string, fallback: string) => string
) => {
  const option = WORK_AVAILABILITY_PRIORITIES.find(
    (item) => item.value === parseWorkAvailabilityPriority(priority)
  );
  return option ? t(option.labelKey, option.labelFallback) : '';
};

type DrawerState =
  | { mode: 'create'; id: null }
  | { mode: 'edit'; id: number }
  | null;

export interface WorkAvailabilityProps {
  workId: number;
  services: IWorkAvailabilityServices;
  setIsInProgress: (value: boolean) => void;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
  readOnly?: boolean;
}

const WorkAvailability = ({
  workId,
  services,
  setIsInProgress,
  setSuccessMessage,
  setErrorMessage,
  readOnly = false,
}: WorkAvailabilityProps) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const cardListRef = useRef<PagedCardListRef>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const columns: DataGridColumn<WorkAvailabilityListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        name: t('id', 'Id'),
        width: 70,
        isSortable: true,
      },
      {
        key: 'startAt',
        name: t('start', 'Start'),
        width: 170,
        isSortable: true,
        renderCell: (item) =>
          item.localStartAt
            ? item.localStartAt.slice(0, 16).replace('T', ' ')
            : formatDate(item.startAt, true),
      },
      {
        key: 'endAt',
        name: t('end', 'End'),
        width: 170,
        isSortable: true,
        renderCell: (item) =>
          item.localEndAt
            ? item.localEndAt.slice(0, 16).replace('T', ' ')
            : formatDate(item.endAt, true),
      },
      {
        key: 'duration',
        name: t('duration', 'Duration'),
        width: 110,
        renderCell: (item) =>
          item.duration == null
            ? ''
            : formatWorkAvailabilityDuration(item.duration),
      },
      {
        key: 'timezoneId',
        name: t('timezone', 'Timezone'),
        width: 180,
        isSortable: true,
      },
      {
        key: 'priority',
        name: t('priority', 'Priority'),
        width: 150,
        isSortable: true,
        renderCell: (item) => priorityLabel(item.priority, t),
      },
      {
        key: 'sourceControlled',
        name: t('source', 'Source'),
        width: 140,
      },
      {
        key: 'status',
        name: t('status', 'Status'),
        width: 100,
        renderCell: (item) =>
          item.status ? t('active', 'Active') : t('inactive', 'Inactive'),
      },
      {
        key: 'isAllDay',
        name: t('allDay', 'All day'),
        width: 90,
        renderCell: (item) => (item.isAllDay ? t('yes', 'Yes') : t('no', 'No')),
      },
    ],
    [t]
  );

  const openCreate = useCallback(() => {
    setDrawer({ mode: 'create', id: null });
  }, []);

  const openEdit = useCallback((id: number) => {
    setDrawer({ mode: 'edit', id });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawer(null);
  }, []);

  const refreshList = useCallback(() => {
    gridRef.current?.refresh();
    cardListRef.current?.refresh();
  }, []);

  const toCardItem = useCallback(
    (item: IWorkAvailabilityWindow): Record<string, unknown> => ({
      ...item,
      code: `#${item.id}`,
      name: `${
        item.localStartAt
          ? item.localStartAt.slice(0, 16).replace('T', ' ')
          : formatDate(item.startAt, true)
      } – ${formatDate(item.endAt, true)}`,
      description: [
        item.duration == null
          ? null
          : formatWorkAvailabilityDuration(item.duration),
        item.timezoneId,
        priorityLabel(item.priority, t),
      ]
        .filter(Boolean)
        .join(' · '),
      status: item.status ? t('active', 'Active') : t('inactive', 'Inactive'),
    }),
    [t]
  );

  const handleSaved = useCallback(() => {
    setSuccessMessage(
      drawer?.mode === 'create'
        ? t('availabilityCreated', 'Availability window created.')
        : t('availabilityUpdated', 'Availability window updated.')
    );
    closeDrawer();
    refreshList();
  }, [closeDrawer, drawer?.mode, refreshList, setSuccessMessage, t]);

  const handleDelete = useCallback(async () => {
    if (deleteTargetId == null) return;
    setDeleting(true);
    try {
      const abortController = new AbortController();
      await services.deleteWorkAvailabilityWindow(
        abortController,
        deleteTargetId
      );
      setSuccessMessage(
        t('availabilityDeleted', 'Availability window deleted.')
      );
      setDeleteTargetId(null);
      setSelectedId(null);
      refreshList();
    } catch (err: unknown) {
      setErrorMessage(
        getApiErrorMessage(
          err,
          t('failedToDeleteWindow', 'Failed to delete availability window.')
        )
      );
    } finally {
      setDeleting(false);
    }
  }, [
    deleteTargetId,
    refreshList,
    services,
    setErrorMessage,
    setSuccessMessage,
    t,
  ]);

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: t('refresh', 'Refresh'),
        icon: <ArrowClockwiseDashesColor />,
        onClick: (ctx: ICommandBarContext) => ctx.refresh(),
      },
      ...(!readOnly
        ? [
            {
              key: 'create',
              text: t('create', 'Create'),
              icon: <AddCircle24Regular />,
              onClick: () => openCreate(),
            },
            {
              key: 'edit',
              text: t('edit', 'Edit'),
              icon: <Edit24Regular />,
              disabled: selectedId == null,
              onClick: () => {
                if (selectedId != null) openEdit(selectedId);
              },
            },
          ]
        : []),
    ],
    [openCreate, openEdit, readOnly, selectedId, t]
  );

  const cardToolbarItems: ICardCommandBarItem[] = useMemo(
    () =>
      readOnly
        ? []
        : [
            {
              key: 'create',
              text: t('create', 'Create'),
              icon: <AddCircle24Regular />,
              onClick: () => openCreate(),
            },
          ],
    [openCreate, readOnly, t]
  );

  const cardRowMenuOptions: IRowMenuOption<Record<string, unknown>>[] = useMemo(
    () =>
      readOnly
        ? []
        : [
            {
              key: 'edit',
              label: t('edit', 'Edit'),
              icon: <Edit24Regular />,
              onClick: (item) => openEdit(Number(item.id)),
            },
            {
              key: 'delete',
              label: t('delete', 'Delete'),
              icon: <DeleteRegular />,
              onClick: (item) => setDeleteTargetId(Number(item.id)),
            },
          ],
    [openEdit, readOnly, t]
  );

  const paginationBarItems: ICommandBarItem[] = useMemo(
    () =>
      readOnly
        ? []
        : [
            {
              key: 'delete',
              text: t('delete', 'Delete'),
              icon: <DeleteRegular />,
              disabled: selectedId == null,
              onClick: () => {
                if (selectedId != null) setDeleteTargetId(selectedId);
              },
            },
          ],
    [readOnly, selectedId, t]
  );

  const empty = useMemo(
    () => ({
      title: t('noAvailabilityYet', 'No availability windows yet!'),
      subtitle: t(
        'emptyAvailabilityHint',
        'Create your first availability window to start tracking preferred times.'
      ),
      action: readOnly
        ? undefined
        : {
            text: t('addAvailability', 'Add availability'),
            onClick: () => openCreate(),
          },
    }),
    [openCreate, readOnly, t]
  );

  const handleListError = useCallback(
    (error: Error) => {
      setErrorMessage(
        getApiErrorMessage(
          error,
          t('failedToLoadWindows', 'Failed to load availability windows.')
        )
      );
    },
    [setErrorMessage, t]
  );

  return (
    <div style={isMobile ? undefined : { overflowX: 'auto' }}>
      <FluentProvider theme={theme} style={{ background: 'transparent' }}>
        {isMobile ? (
          <PagedCardList
            ref={cardListRef}
            entity="WorkAvailabilityWindow"
            fallbackField="name"
            getAction={async (
              abortController,
              pageSize,
              pageNo,
              search,
              filters
            ) => {
              const result = await services.getWorkAvailabilityWindows(
                abortController,
                workId,
                { pageSize, pageNo, search, filters }
              );
              return {
                ...result,
                items: result.items.map(toCardItem),
              } as IPagedCollection<Record<string, unknown>>;
            }}
            pageSize={PAGE_SIZE}
            toolbarItems={cardToolbarItems}
            rowMenuOptions={cardRowMenuOptions}
            onLoading={() => setIsInProgress(true)}
            onLoaded={() => setIsInProgress(false)}
            onError={handleListError}
            empty={empty}
          />
        ) : (
          <PagedDataGrid<WorkAvailabilityListItem>
            ref={gridRef}
            columns={columns}
            commandBarItems={commandBarItems}
            paginationBarItems={paginationBarItems}
            selectionMode="single"
            pageSize={PAGE_SIZE}
            onSelectionChange={(item) => setSelectedId(item?.id ?? null)}
            onRowDoubleClicked={(item) => openEdit(item.id)}
            getAction={async (
              abortController,
              pageSize,
              pageNo,
              orderBy,
              search,
              filters
            ) => {
              const result = await services.getWorkAvailabilityWindows(
                abortController,
                workId,
                { pageSize, pageNo, orderBy, search, filters }
              );
              return {
                ...result,
                items: result.items as WorkAvailabilityListItem[],
              };
            }}
            onLoading={() => setIsInProgress(true)}
            onLoaded={() => setIsInProgress(false)}
            onError={handleListError}
            empty={empty}
          />
        )}
      </FluentProvider>

      <WorkAvailabilityDrawer
        open={drawer != null}
        mode={drawer?.mode ?? 'create'}
        workId={workId}
        windowId={drawer?.id ?? null}
        services={services}
        onClose={closeDrawer}
        onSaved={handleSaved}
      />

      <Dialog
        open={deleteTargetId != null}
        onOpenChange={(_, data) => {
          if (!data.open && !deleting) setDeleteTargetId(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t('deleteAvailability', 'Delete availability window?')}
            </DialogTitle>
            <DialogContent>
              {t(
                'deleteAvailabilityConfirm',
                'This availability window will be removed.'
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteTargetId(null)}
                disabled={deleting}
              >
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleDelete}
                disabled={deleting}
              >
                {t('delete', 'Delete')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default WorkAvailability;
